import { usePlannerEventSignUpForm } from "./usePlannerEventSignUpForm";
import { SubmitHandler } from "react-hook-form";
import { IPlannerEventSignUpFormModel } from "../models/plannerEventSignUpFormModel";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useNavigate, useParams } from "react-router-dom";
import { constructPlannerEventHref } from "../../../../common/infrastructure/routes/routePaths";
import { PageMode } from "../../../../common/types";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { IGetSignUpEventDataViewModel } from "../models/getSignUpEventDataViewModel";
import { useEffect, useMemo, useState } from "react";
import {
  getPaymentOptions,
  parsePaymentMethodId,
  paymentTypesWithCustomPrice,
  sortPaymentOptionsByTypeOrder,
} from "../../../../common/utilities/paymentUtility";
import { PaymentTypes } from "../../../../common/enums";
import { IEventSelectFieldModel } from "../models/eventSelectFieldModel";
import { IStudioSettingsModel } from "../../../../common/models/IStudioSettingsModel";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { useMeetingLinkRecipientsWarning } from "../../common/hooks/useMeetingLinkRecipientsWarning";
import { filterRelatedEvents } from "../utils/relatedEventsFilterUtility";

export const usePlannerEventSignUpPage = (pageMode: PageMode) => {
  const { eventId } = useParams();

  const pageTitle =
    pageMode === "add" ? "Записване за клас" : "Редактиране на записването";
  const buttonText = pageMode === "add" ? "Добавяне" : "Редактиране";

  const { data: event, isLoading: isEventLoading } =
    useApiQuery<IGetSignUpEventDataViewModel>({
      queryKey: [endpointsMetadata.planner.getSignUpEventData, eventId],
      endpoint: `${endpointsMetadata.planner.getSignUpEventData}?eventId=${eventId}`,
      enabled: eventId !== undefined,
      disableErrorToast: false,
      retry: false,
    });

  const { data: usersResult, isLoading: isUsersLoading } = useApiQuery<
    IUserDropdownOption[]
  >({
    queryKey: [
      endpointsMetadata.planner.getAvailableEventUsersWihoutDeleted,
      eventId,
    ],
    endpoint: `${endpointsMetadata.planner.getAvailableEventUsersWihoutDeleted}?eventId=${eventId}`,
    enabled: eventId !== undefined,
  });

  const { data: allRelatedEvents } =
    useApiQuery<IEventSelectFieldModel[]>({
      queryKey: [endpointsMetadata.planner.getAllRelatedEventsById, eventId],
      endpoint: `${endpointsMetadata.planner.getAllRelatedEventsById}?eventId=${eventId}`,
      enabled: eventId !== undefined,
    });

  // 1 = same name, 2 = same name and start hour, 3 = all future
  const [filterOption, setFilterOption] = useState<number>(1);

  // Get the hour from the current event's date
  const currentEventHour = useMemo(() => {
    if (!event?.date) return null;
    return new Date(event.date).getHours();
  }, [event?.date]);

  // A class is matched by its name, not its series: duplicating a class starts a new series,
  // and staff still want to sign a client up for every group of it ahead.
  const relatedEvents = useMemo(() => {
    if (!allRelatedEvents) return undefined;

    return filterRelatedEvents(
      allRelatedEvents,
      filterOption,
      event?.name,
      currentEventHour,
    );
  }, [allRelatedEvents, filterOption, event?.name, currentEventHour]);

  const form = usePlannerEventSignUpForm(
    event?.id,
    event?.eventPrice,
    usersResult,
  );

  const isRecurring = form.watch("isRecurring");
  const markAsArrivedAutomatically = form.watch("markAsArrivedAutomatically");
  const selectedUserIds = form.watch("userIds");
  const selectedEventIds = form.watch("eventIds");
  const selectedPaymentMethodId = form.watch("paymentTypeId");
  const { paymentTypeId } = parsePaymentMethodId(selectedPaymentMethodId);

  const enableCustomPriceInput =
    paymentTypesWithCustomPrice.includes(paymentTypeId);

  const isAutoConfirmDisabled = false;

  const { data: tenantSettings } = useApiQuery<IStudioSettingsModel>({
    queryKey: [endpointsMetadata.studio.getStudioSettings],
    endpoint: endpointsMetadata.studio.getStudioSettings,
  });

  const groupWithPlan = useMemo(() => {
    return usersResult?.filter((item) => item.hasPlan) ?? [];
  }, [usersResult]);
  const groupWithoutPlan = useMemo(() => {
    return usersResult?.filter((item) => !item.hasPlan) ?? [];
  }, [usersResult]);

  // TODO: Fix the users' dropdown hover bug
  const users = useMemo(() => {
    const groupedOptions = [];

    if (groupWithPlan.length > 0) {
      groupedOptions.push({
        label: "С абонаментна карта",
        items: groupWithPlan,
      });
    }
    if (groupWithoutPlan.length > 0) {
      groupedOptions.push({
        label: "Без абонаментна карта",
        items: groupWithoutPlan,
      });
    }

    return groupedOptions;
  }, [usersResult, groupWithPlan, groupWithoutPlan]);

  const paymentOptions = useMemo(() => {
    if (!selectedUserIds?.length || !event?.acceptedPlanPaymentIds) {
      return [];
    }

    const selectedUsers = usersResult?.filter((user) =>
      selectedUserIds.includes(user.id),
    );

    if (!selectedUsers?.length) return [];

    const commonPlanTypeIds = selectedUsers
      .map((user) => (user.planTypes ?? []).map((pt) => pt.id))
      .reduce((acc, curr) => acc.filter((id) => curr.includes(id)));

    // Collect all events being registered: always the current event + any selected related events
    const selectedRelatedEvents =
      isRecurring && selectedEventIds?.length
        ? (allRelatedEvents?.filter((e) =>
            selectedEventIds.includes(e.id),
          ) ?? [])
        : [];

    // A plan type is valid only if ALL events accept it
    // (event accepts a plan if its acceptedPlanPaymentIds is empty = accepts all, or includes that plan)
    const allRegisteredEvents = [
      { acceptedPlanPaymentIds: event.acceptedPlanPaymentIds },
      ...selectedRelatedEvents,
    ];

    const validPlanTypeIds = commonPlanTypeIds.filter((planId) =>
      allRegisteredEvents.every(
        (e) =>
          e.acceptedPlanPaymentIds.length === 0 ||
          e.acceptedPlanPaymentIds.includes(planId),
      ),
    );

    const planTypeOptions =
      selectedUsers[0]?.planTypes
        .filter((pt) => validPlanTypeIds.includes(pt.id))
        .map((pt) => ({
          id: `${PaymentTypes.Plan}_${pt.id}`,
          name: `Абонаментен план (${pt.name})`,
        })) ?? [];

    const filteredBaseOptions = getPaymentOptions()
      .filter((opt) => opt.id !== PaymentTypes.Plan)
      .filter((opt) =>
        tenantSettings?.paymentTypes?.some((y) => y.paymentType === opt.id),
      );
    const baseOptions = sortPaymentOptionsByTypeOrder(
      filteredBaseOptions,
      tenantSettings?.paymentTypes?.map((y) => y.paymentType) ?? [],
    );

    const finalOptions = [...planTypeOptions, ...baseOptions];
    return finalOptions;
  }, [tenantSettings, selectedUserIds, selectedEventIds, isRecurring, allRelatedEvents, usersResult, event]);

  // Reason why subscription plan options are hidden (null = they are visible or no users selected)
  const subscriptionPlanHiddenReason = useMemo(() => {
    if (!selectedUserIds?.length || !event) return null;

    const selectedUsers = usersResult?.filter((user) =>
      selectedUserIds.includes(user.id),
    );
    if (!selectedUsers?.length) return null;

    const commonPlanTypeIds = selectedUsers
      .map((user) => (user.planTypes ?? []).map((pt) => pt.id))
      .reduce((acc, curr) => acc.filter((id) => curr.includes(id)));

    if (commonPlanTypeIds.length === 0) {
      return "Абонаментният план не е наличен, тъй като избраните потребители нямат общ абонаментен план.";
    }

    const selectedRelatedEvents =
      isRecurring && selectedEventIds?.length
        ? (allRelatedEvents?.filter((e) => selectedEventIds.includes(e.id)) ?? [])
        : [];

    const allRegisteredEvents = [
      { acceptedPlanPaymentIds: event.acceptedPlanPaymentIds },
      ...selectedRelatedEvents,
    ];

    const validPlanTypeIds = commonPlanTypeIds.filter((planId) =>
      allRegisteredEvents.every(
        (e) =>
          e.acceptedPlanPaymentIds.length === 0 ||
          e.acceptedPlanPaymentIds.includes(planId),
      ),
    );

    if (validPlanTypeIds.length === 0) {
      return "Абонаментният план не е наличен, тъй като не всички избрани класове го поддържат.";
    }

    return null;
  }, [selectedUserIds, usersResult, isRecurring, selectedEventIds, allRelatedEvents, event]);

  // Set default payment type when payment options change or auto-confirm is enabled
  useEffect(() => {
    if (!markAsArrivedAutomatically || !paymentOptions?.length) {
      return;
    }

    const firstOptionId = paymentOptions[0].id;
    form.setValue("paymentTypeId", String(firstOptionId));
  }, [paymentOptions, markAsArrivedAutomatically]);

  // Reset selected events when filter option changes
  useEffect(() => {
    form.setValue("eventIds", []);
  }, [filterOption]);

  const isLoading = isUsersLoading || isEventLoading;

  const { mutate } = useApiMutation();
  const { warnClientsWithoutEmail } = useMeetingLinkRecipientsWarning();

  const navigate = useNavigate();

  const submitRegistration = (data: IPlannerEventSignUpFormModel) => {
    const { paymentTypeId, planTypeId } = parsePaymentMethodId(
      data.paymentTypeId,
    );

    // Determine which events to register for
    // ALWAYS include the current event, plus any selected related events
    const eventIdsToRegister = data.isRecurring
      ? [Number(eventId), ...data.eventIds] // Current event + selected related events
      : [Number(eventId)]; // Just current event

    // Create a payload with proper field mappings for the backend
    const payload = {
      userIds: data.userIds,
      eventIds: eventIdsToRegister,
      isRecurring: data.isRecurring || false,
      markAsArrivedAutomatically: data.markAsArrivedAutomatically || false,
      paymentTypeId: Number(paymentTypeId) || 0,
      paymentPlanTypeId: planTypeId ? Number(planTypeId) : null,
      price: data.price || 0,
      currencyId: data.currencyId || null,
    };

    mutate(
      {
        endpoint: endpointsMetadata.planner.registerForEvent,
        globalIsLoading: true,
        method: "post",
        payload: payload,
      },
      {
        onSuccess: () => {
          // The clients of a remote class are emailed its Meet link; tell the staff member
          // about the ones just registered who have no email.
          const registeredUserIds = (data.userIds ?? []).map(Number);
          if (event?.isRemote && registeredUserIds.length > 0) {
            warnClientsWithoutEmail(Number(eventId), registeredUserIds);
          }

          navigate(constructPlannerEventHref(Number(eventId)));
        },
      },
    );
  };

  const onSave: SubmitHandler<IPlannerEventSignUpFormModel> = (data) => {
    if (event?.requiresPrepayment) {
      const isStripe = event.prepaymentProvider === 2;
      const message = isStripe
        ? "Този клас изисква предварително плащане чрез Stripe. Системата няма да изпрати автоматичен линк за плащане. Ако потребителят трябва да плати, уверете се, че плащането е извършено по друг начин (абонаментен план, в брой и т.н.)."
        : "Моля имайте предвид, че тъй като събитието изисква предварително плащане, ако някой от потребителите, които ще запишете няма актуален имейл адрес, или не е дал съгласието си за получаване на автоматизирани имейли от системата, няма да получи линк за плащане.";

      showCustomConfirmDialog(
        message,
        () => submitRegistration(data),
        undefined,
        "warning",
        "Разбрах",
        "Отказ"
      );
    } else {
      submitRegistration(data);
    }
  };

  return {
    isLoading,
    event,
    paymentOptions,
    buttonText,
    pageTitle,
    form,
    users,
    relatedEvents,
    isRecurring,
    markAsArrivedAutomatically,
    enableCustomPriceInput,
    isAutoConfirmDisabled,
    anyUsersSelected: !!selectedUserIds && selectedUserIds.length > 0,
    hasAnyRelatedEvents: !!allRelatedEvents && allRelatedEvents.length > 0,
    filterOption,
    setFilterOption,
    subscriptionPlanHiddenReason,
    onSave,
  };
};
